// TIP-611 — the order form's state.
// One hook holds all 24 fields so the modal below it is layout and copy only.
// Three things here are rules rather than plumbing, and each is commented where
// it happens: the idempotency key, the name guess, and the credit-exposure
import { useCallback, useEffect, useRef, useState } from "react";
import { DEFAULT_DIAL, OPTION_OTHER, ZONE_OTHER } from "./orderFormFixtures";
import { PROFILE_FIELDS } from "./requesterProfile";
import { lockedFieldsFor } from "./requesterLookup";
import {
  canSubmit,
  formatExposure,
  isEmail,
  isUAE,
  locationForCountry,
  missingRequired,
  nameFromEmail,
  referenceSummary,
} from "./orderFormState";

// The reference lookup fires on a settled email, not on every keystroke.
// An address is the whole query, so a partial one can only ever miss, and
// "ahmed.khan@gulfsteel.ae" would otherwise cost 24 requests to type.
const LOOKUP_DEBOUNCE_MS = 400;

// status: idle    — nothing to ask about (the email is not yet an email)
//         pending — asked, waiting. The no-match note must not flash here
//         hit     — filled and locked the fields it carried
//         miss    — a 404, an empty match, or a request that failed. The
//                   existing behaviour takes over: guess the name, say so
const NO_LOOKUP = { status: "idle", contact: null, email: "" };

const newIdempotencyKey = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // jsdom and older Safari have no randomUUID. The key only has to be unique
  // per open, and the backend treats it as an opaque string.
  return `cb-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

// `null` is a profile that has not resolved yet, which is not the same as an
// empty one — but both start the form empty, so they collapse to the same
const initialValues = (input) => ({
  // Section 1. All six arrive from /api/user/me/ and the selected company's
  // details, mobile included when the account carries one.
  // Anything the reads did not carry stays "" rather than falling back to a
  // placeholder: an empty required field stops the submit and asks, whereas a
  // wrong-but-plausible name would sail through and reach an analyst.
  fullName: input?.fullName || "",
  jobTitle: input?.jobTitle || "",
  company: input?.company || "",
  email: input?.email || "",
  dial: input?.dial || DEFAULT_DIAL,
  mobile: input?.mobile || "",
  country: input?.country || "",

  // Section 2. The subject's country is deliberately NOT defaulted, unlike the
  // requester's — defaulting it would silently file half the briefs against the
  subjectName: "",
  licence: "",
  zone: "",
  zoneOther: "",
  emirate: "",
  subjectCountry: "",
  website: "",

  // The nested optional panel.
  refEmail: "",
  refName: "",
  refRole: "",
  refDial: DEFAULT_DIAL,
  refPhone: "",

  purpose: "",
  purposeOther: "",
  relationship: "",
  relationshipOther: "",
  exposure: "",
  terms: "",
  notes: "",

  declaration: false,
});

/**
 * @param profile          section 1's fields, from /me/ and the company read
 * @param lookupReference  async (email, { fallbackDial }) =>
 *                           { found, contact?, failed? }
 *                         Injected rather than reached for, so this hook and
 *                         the modal below it stay store-free and testable
 *                         without a Provider. Omitted, the reference panel
 *                         behaves exactly as it did before the endpoint
 *                         existed: no match, name guessed from the email.
 */
export const useOrderForm = (profile, { lookupReference, checkDuplicateSubject } = {}) => {
  const [values, setValues] = useState(() => initialValues(profile));

  // Which of the profile's fields the customer has edited. A value they have
  // corrected is theirs, and a late-arriving read must not overwrite it.
  const editedRef = useRef(new Set());
  // The freezone/emirate answers stashed when the pair locked, so unlocking
  // puts back what the customer had rather than a blank. Null whenever the
  const savedLocationRef = useRef(null);
  // Which fields currently hold a value the PROFILE put there. The two reads
  // behind section 1 resolve independently, so an early one can install a
  // fallback that a later one should replace — the company name arrives from
  // /api/user/me/ as the employer, then from the company read as the business
  // actually being acted as. Without this the fallback would stick.
  const fromProfileRef = useRef(new Set());
  // Once the customer types in the contact name, the guess stops for good. It
  // is their correction; re-deriving it on the next keystroke in the email
  const [refNameTouched, setRefNameTouched] = useState(false);
  // The reference lookup's outcome for the email currently in the box.
  const [lookup, setLookup] = useState(NO_LOOKUP);
  // The fields a match filled, and which are therefore not editable. Held as
  // state rather than derived from `lookup` so that clearing the email clears
  // the locks in the same render the fields empty in.
  const [lockedFields, setLockedFields] = useState([]);
  // Read by the lookup without making the request depend on it: the dial code
  // is only a FALLBACK for a national number, and changing the picker must not
  const dialRef = useRef(values.refDial);
  dialRef.current = values.refDial;
  const [showErrors, setShowErrors] = useState(false);
  // TIP-611 — per-field messages the BACKEND sent, keyed by form field. Kept
  // apart from `showErrors`, which is only about the six required fields the
  // browser can check for itself.
  const [serverErrors, setServerErrors] = useState({});

  // "You already have a brief for this business." A WARNING, not an error: it
  // never blocks the submit and is kept well away from `serverErrors`, which is
  // about values the backend refused. `checkedName` is the name the standing
  // warning belongs to, so editing the box clears it and the customer is not
  // left reading a sentence about a company they have moved on from.
  const [duplicate, setDuplicate] = useState({ warning: null, checkedName: "" });
  // The name a request has been SENT for, written synchronously so that two
  // blurs in the same tick cannot both get past the guard — React has not
  // committed the state above by the time the second one runs.
  const duplicateAskedRef = useRef("");
  const duplicateRunRef = useRef(0);

  // The key is minted when the form OPENS, not on submit, so that a double
  // press of Send request, or a retry after a dropped response, is the same
  // order to the backend and cannot spend two briefs.
  const idempotencyKey = useRef(newIdempotencyKey());

  // A field the customer has just edited no longer carries the server's
  // complaint about its old value: leaving the message up would mark a field
  // that has already been fixed.
  // The two reads behind section 1 usually resolve after the modal is already
  // open, so the form adopts them when they land rather than waiting for them.
  // Only untouched, still-empty fields are filled — the customer's own typing
  // always wins, and a field already carrying a value is never rewritten.
  useEffect(() => {
    if (!profile) return;
    setValues((current) => {
      let next = current;
      PROFILE_FIELDS.forEach((key) => {
        const incoming = profile[key];
        // The customer's own typing always wins.
        if (!incoming || editedRef.current.has(key)) return;
        // Fillable when empty, when the value there came from an earlier read,
        // or when it is only the default dial code the form started with.
        const replaceable =
          !current[key] ||
          fromProfileRef.current.has(key) ||
          (key === "dial" && current.dial === DEFAULT_DIAL);
        if (!replaceable || current[key] === incoming) return;
        if (next === current) next = { ...current };
        next[key] = incoming;
        fromProfileRef.current.add(key);
      });
      return next;
    });
  }, [profile]);

  const clearServerError = useCallback(
    (key) =>
      setServerErrors((s) => {
        if (!(key in s)) return s;
        const { [key]: _gone, ...rest } = s;
        return rest;
      }),
    []
  );

  const set = useCallback(
    (key) => (v) => {
      editedRef.current.add(key);
      setValues((s) => {
        // TIP-611 — the subject's country governs the freezone and emirate
        // above it: both lists are UAE vocabularies, so a company outside the
        // UAE gets the two answers that are actually true of it.
        if (key !== "subjectCountry") return { ...s, [key]: v };

        const { values: location, saved } = locationForCountry(
          v,
          s,
          savedLocationRef.current
        );
        savedLocationRef.current = saved;
        return { ...s, subjectCountry: v, ...location };
      });
      clearServerError(key);
      if (key === "subjectCountry") {
        clearServerError("zone");
        clearServerError("emirate");
      }
    },
    [clearServerError]
  );
  const onInput = useCallback(
    (key) => (e) => {
      editedRef.current.add(key);
      const next = e.target.value;
      setValues((s) => ({ ...s, [key]: next }));
      clearServerError(key);
      // Editing the subject name retires the warning about the old one. It is
      // re-asked when the customer leaves the box again.
      if (key === "subjectName") {
        duplicateAskedRef.current = "";
        setDuplicate((d) => (d.warning || d.checkedName ? { warning: null, checkedName: "" } : d));
      }
    },
    [clearServerError]
  );

  /* Ask about the name the customer has just finished typing. */
  const onSubjectNameBlur = useCallback(async () => {
    const name = values.subjectName.trim();

    if (!name) {
      duplicateAskedRef.current = "";
      setDuplicate({ warning: null, checkedName: "" });
      return;
    }
    if (name === duplicateAskedRef.current || !checkDuplicateSubject) return;

    // Claimed BEFORE the await, so a second blur on the same name is dropped
    // here rather than racing the first request's answer.
    duplicateAskedRef.current = name;
    const run = ++duplicateRunRef.current;
    const warning = await checkDuplicateSubject(name);
    if (run !== duplicateRunRef.current) return;
    setDuplicate({ warning, checkedName: name });
  }, [values.subjectName, checkDuplicateSubject]);

  const onExposure = useCallback(
    (e) => {
      setValues((s) => ({ ...s, exposure: formatExposure(e.target.value) }));
      clearServerError("exposure");
    },
    [clearServerError]
  );

  const onRefEmail = useCallback(
    (e) => {
      const next = e.target.value;
      clearServerError("refEmail");
      setValues((s) => ({
        ...s,
        refEmail: next,
        // The guess still happens IMMEDIATELY, exactly as before: the lookup
        // takes a moment and a valid email should not leave the name box empty
        // in the meantime. A match overwrites it when one arrives.
        refName: !refNameTouched ? (isEmail(next) ? nameFromEmail(next) : "") : s.refName,
      }));
      // A new address invalidates the previous answer, and with it the locks —
      // the fields must be editable again the instant the email changes, or a
      // corrected address would leave the old contact's details stuck in place.
      setLookup(NO_LOOKUP);
      setLockedFields([]);
    },
    [refNameTouched, clearServerError]
  );

  const onRefName = useCallback(
    (e) => {
      setRefNameTouched(true);
      setValues((s) => ({ ...s, refName: e.target.value }));
      clearServerError("refName");
    },
    [clearServerError]
  );

  const refEmailValid = isEmail(values.refEmail);

  // TIP-611 — ask the directory once the email has settled.
  // Debounced, and only for a syntactically valid address. The timer is torn
  // down on every change, so a customer still typing never fires a request,
  // and `cancelled` drops a response that arrives after the email moved on —
  // otherwise a slow answer for the previous address would fill and lock the
  // fields under the new one.
  useEffect(() => {
    const email = values.refEmail.trim();
    if (!lookupReference || !isEmail(email)) {
      setLookup((l) => (l.status === "idle" ? l : NO_LOOKUP));
      return undefined;
    }
    // Already answered for this exact address: nothing to ask again.
    if (lookup.email === email && lookup.status !== "idle") return undefined;

    let cancelled = false;
    setLookup({ status: "pending", contact: null, email });

    const timer = setTimeout(async () => {
      const result = await lookupReference(email, { fallbackDial: dialRef.current });
      if (cancelled) return;

      if (!result?.found || !result.contact) {
        // A miss, an empty match and a failed request are one outcome to the
        // customer: we could not tell them anything, so they type it in.
        setLookup({ status: "miss", contact: null, email });
        setLockedFields([]);
        return;
      }

      const contact = result.contact;
      setLookup({ status: "hit", contact, email });
      setValues((s) => ({
        ...s,
        // Only what the match actually carried. A blank job title in the
        // response must not wipe one the customer had already typed.
        refName: contact.name || s.refName,
        refRole: contact.role || s.refRole,
        refDial: contact.dialFromLookup ? contact.dial : s.refDial,
        refPhone: contact.phone || s.refPhone,
      }));
      // The filled fields are the directory's answer, so they are locked; the
      // ones it had nothing for stay empty and editable.
      setLockedFields(lockedFieldsFor(contact));
      // These are now the contact's real details, not a guess, so editing the
      // email later must not let the guess reclaim the name.
      setRefNameTouched(true);
    }, LOOKUP_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // `lookup.email`/`lookup.status` are read to avoid re-asking for an address
    // already answered; they cannot re-trigger it, because the branch that
    // uses them returns without scheduling anything.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.refEmail, lookupReference]);

  const reset = useCallback(() => {
    // Order another brief keeps the requester's own details — they are the same
    // person, at the same company — and clears everything about the subject.
    editedRef.current = new Set();
    fromProfileRef.current = new Set(PROFILE_FIELDS);
    savedLocationRef.current = null;
    setValues(initialValues(profile));
    setRefNameTouched(false);
    setLookup(NO_LOOKUP);
    setLockedFields([]);
    setDuplicate({ warning: null, checkedName: "" });
    duplicateAskedRef.current = "";
    duplicateRunRef.current += 1;
    setShowErrors(false);
    setServerErrors({});
    // A second order is a second charge, so it gets its own key.
    idempotencyKey.current = newIdempotencyKey();
  }, [profile]);

  return {
    values,
    set,
    onInput,
    onExposure,
    onSubjectNameBlur,
    // The duplicate-brief sentence for the subject-name box, or null. A
    // warning, never an error: `canSubmit` does not read it.
    duplicateWarning: duplicate.warning,
    onRefEmail,
    onRefName,
    reset,

    // TIP-611 — a field the reference lookup filled is not editable. Read by
    // the panel for every one of its four controls; false for everything else,
    // so it is safe to ask about any field.
    isLocked: (key) => lockedFields.includes(key),

    idempotencyKey: idempotencyKey.current,

    // The three conditional fields.
    showZoneOther: values.zone === ZONE_OTHER,
    // True once a country outside the UAE is chosen. Section 2 reads it twice:
    // to disable the freezone and emirate pair, and to say why they answered
    subjectOutsideUAE: !!values.subjectCountry && !isUAE(values.subjectCountry),
    showPurposeOther: values.purpose === OPTION_OTHER,
    showRelationshipOther: values.relationship === OPTION_OTHER,

    // The reference panel's three behaviours. Nothing below the email exists
    // until the value looks like an email address.
    refEmailValid,
    // The matched contact, for the green panel's sentence. Its details are
    // already in the fields — there is nothing left to press.
    directoryHit: lookup.status === "hit" ? lookup.contact : null,
    // In flight. The panel says so rather than showing the no-match note for
    // the moment before the answer lands.
    lookupPending: lookup.status === "pending",
    // A valid email with no match to show. Deliberately NOT `status === "miss"`:
    // that would leave the note off entirely when no lookup is wired at all
    // (no company selected, or the endpoint not injected), and the guessed name
    // would then sit there with nothing saying it was guessed. The only state
    // it stays out of is `pending`, where the answer is still coming.
    showGuessNote:
      refEmailValid && lookup.status !== "hit" && lookup.status !== "pending",
    referenceSummary: referenceSummary(values.refEmail, values.refName),

    canSubmit: canSubmit(values),
    missing: missingRequired(values),
    showErrors,
    setShowErrors,

    // What the backend rejected, and by field.
    serverErrors,
    setServerErrors,
    clearServerError,
    errorFor: (key) => serverErrors[key] ?? null,
  };
};

export default useOrderForm;
